/**
 * Hook for logging navigation events automatically
 * Tracks route changes and provides navigation functions with built-in logging
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { logNavigation } from '../utils/cloudLogger';

export const useNavigationLogger = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const previousPath = useRef(null);

    // Log route changes automatically
    useEffect(() => {
        const currentPath = location.pathname;
        const fromPath = previousPath.current;
        
        // Don't log the initial page load (when fromPath is null)
        if (fromPath !== null && fromPath !== currentPath) {
            logNavigation(fromPath, currentPath, {
                trigger: 'route_change',
                search: location.search,
                hash: location.hash,
                state: location.state
            });
        }
        
        previousPath.current = currentPath;
    }, [location]);

    // Wrapped navigate function with logging
    const loggedNavigate = (to, options = {}) => {
        const fromPath = location.pathname;
        const toPath = typeof to === 'string' ? to : to.pathname;
        
        logNavigation(fromPath, toPath, {
            trigger: 'programmatic',
            navigateOptions: options,
            userInitiated: options.userInitiated !== false // default to true unless explicitly set to false
        });
        
        navigate(to, options);
    };

    // Log click-based navigation
    const logClickNavigation = (to, additionalData = {}) => {
        const fromPath = location.pathname;
        const toPath = typeof to === 'string' ? to : to.pathname;
        
        logNavigation(fromPath, toPath, {
            trigger: 'click',
            ...additionalData,
            userInitiated: true
        });
        
        navigate(to);
    };

    // Log section changes within the same page
    const logSectionChange = (sectionName, additionalData = {}) => {
        const currentPath = location.pathname;
        
        logNavigation(currentPath, `${currentPath}#${sectionName}`, {
            trigger: 'section_change',
            section: sectionName,
            ...additionalData,
            userInitiated: true
        });
    };

    return {
        navigate: loggedNavigate,
        clickNavigate: logClickNavigation,
        logSectionChange,
        currentPath: location.pathname
    };
};
